import { FC, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import { Save, Cancel, RestartAlt } from "@mui/icons-material";
import ControlledColorField from "../../components/controlledComponents/ControlledColorField";
import { TBranding } from "../../../apis/branding/branding.interfaces";
import {
  useUpdateBrandingHook,
} from "../../../hooks/useBrandingHook";
import { logger } from "../../../utils/common";

// Default colors
const DEFAULT_PRIMARY_COLOR = "#3a36b8";
const DEFAULT_SECONDARY_COLOR = "#06B6D4";
const DEFAULT_ACCENT_COLOR = "#ff9800";

// Hex color validation
const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

// Calculate relative luminance for contrast checking
const getLuminance = (hex: string): number => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((val) => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Calculate contrast ratio between two colors
const getContrastRatio = (color1: string, color2: string): number => {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
};

// Convert hex to RGB
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

// Ensure hex color has # prefix
const normalizeHexColor = (color: string): string => {
  if (!color || color.length === 0) return "#000000";
  const cleaned = color.replace("#", "").toUpperCase();
  if (cleaned.length !== 6) return "#000000";
  return `#${cleaned}`;
};

const colorSchemeSchema = z
  .object({
    primaryColor: z
      .string()
      .min(1, "Primary color is required")
      .min(6, "Hex color must be 6 characters")
      .max(6, "Hex color must be 6 characters")
      .refine(
        (val) => {
          const normalized = normalizeHexColor(val);
          return hexColorRegex.test(normalized);
        },
        { message: "Please enter a valid hex color (e.g., 3a36b8)" },
      ),
    secondaryColor: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val.length === 0) return true;
          if (val.length !== 6) return false;
          const normalized = normalizeHexColor(val);
          return hexColorRegex.test(normalized);
        },
        { message: "Please enter a valid hex color (e.g., 06B6D4)" },
      ),
    accentColor: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val.length === 0) return true;
          if (val.length !== 6) return false;
          const normalized = normalizeHexColor(val);
          return hexColorRegex.test(normalized);
        },
        { message: "Please enter a valid hex color (e.g., ff9800)" },
      ),
  })
  .refine(
    (data) => {
      // Only validate if both colors are complete (6 characters)
      if (data.primaryColor.length !== 6) return true;
      if (!data.secondaryColor || data.secondaryColor.length !== 6) return true;

      const primary = normalizeHexColor(data.primaryColor);
      const secondary = normalizeHexColor(data.secondaryColor);

      // Check contrast between primary and secondary
      const contrast = getContrastRatio(primary, secondary);
      return contrast >= 3.0; // WCAG AA standard for large text
    },
    {
      message:
        "Primary and secondary colors must have sufficient contrast (minimum 3:1 ratio)",
      path: ["secondaryColor"],
    },
  )
  .refine(
    (data) => {
      // Only validate if primary color is complete
      if (data.primaryColor.length !== 6) return true;

      const primary = normalizeHexColor(data.primaryColor);
      const white = "#FFFFFF";
      const contrast = getContrastRatio(primary, white);
      return contrast >= 4.5; // WCAG AA standard for normal text
    },
    {
      message:
        "Primary color must have sufficient contrast with white text (minimum 4.5:1 ratio)",
      path: ["primaryColor"],
    },
  );

type ColorSchemeFormData = z.infer<typeof colorSchemeSchema>;

interface ColorSchemeDialogProps {
  open: boolean;
  onClose: () => void;
  branding?: TBranding | null;
}

const ColorSchemeDialog: FC<ColorSchemeDialogProps> = ({
  open,
  onClose,
  branding,
}) => {
  const updateBrandingMutation = useUpdateBrandingHook();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ColorSchemeFormData>({
    resolver: zodResolver(colorSchemeSchema),
    defaultValues: {
      primaryColor: (branding?.primaryColor ?? DEFAULT_PRIMARY_COLOR).replace("#", ""),
      secondaryColor: (branding?.secondaryColor ?? DEFAULT_SECONDARY_COLOR).replace("#", ""),
      accentColor: (branding?.accentColor ?? DEFAULT_ACCENT_COLOR).replace("#", ""),
    },
  });

  const watchedColors = watch();

  useEffect(() => {
    if (open && branding) {
      // Remove # prefix if present for form values
      const normalizeForForm = (
        color: string | undefined,
        defaultColor: string,
      ) => {
        if (!color) return defaultColor.replace("#", "");
        return color.startsWith("#") ? color.slice(1) : color;
      };
      reset({
        primaryColor: normalizeForForm(
          branding.primaryColor,
          DEFAULT_PRIMARY_COLOR,
        ),
        secondaryColor: normalizeForForm(
          branding.secondaryColor,
          DEFAULT_SECONDARY_COLOR,
        ),
        accentColor: normalizeForForm(
          branding.accentColor,
          DEFAULT_ACCENT_COLOR,
        ),
      });
    } else if (open) {
      reset({
        primaryColor: DEFAULT_PRIMARY_COLOR.replace("#", ""),
        secondaryColor: DEFAULT_SECONDARY_COLOR.replace("#", ""),
        accentColor: DEFAULT_ACCENT_COLOR.replace("#", ""),
      });
    }
  }, [open, branding, reset]);

  const handleResetToDefaults = () => {
    reset({
      primaryColor: DEFAULT_PRIMARY_COLOR.replace("#", ""),
      secondaryColor: DEFAULT_SECONDARY_COLOR.replace("#", ""),
      accentColor: DEFAULT_ACCENT_COLOR.replace("#", ""),
    });
  };

  const onSubmit = async (data: ColorSchemeFormData) => {
    try {
      const normalizedData = {
        primaryColor: normalizeHexColor(data.primaryColor),
        secondaryColor:
          data.secondaryColor && data.secondaryColor.length === 6
            ? normalizeHexColor(data.secondaryColor)
            : undefined,
        accentColor:
          data.accentColor && data.accentColor.length === 6
            ? normalizeHexColor(data.accentColor)
            : undefined,
      };

      if (branding?.id) {
        await updateBrandingMutation.mutateAsync({
          id: branding.id,
          request: normalizedData,
        });
        logger.show_success("Color scheme updated successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to save color scheme. Please try again.",
      );
    }
  };

  const isSubmitting = updateBrandingMutation.isPending;
  const isError = updateBrandingMutation.isError;
  const errorMessage = updateBrandingMutation.error?.message;

  // Normalize colors for preview
  const previewColors = useMemo(() => {
    return {
      primary: normalizeHexColor(
        watchedColors.primaryColor || DEFAULT_PRIMARY_COLOR.replace("#", ""),
      ),
      secondary:
        watchedColors.secondaryColor &&
        watchedColors.secondaryColor.length === 6
          ? normalizeHexColor(watchedColors.secondaryColor)
          : null,
      accent:
        watchedColors.accentColor && watchedColors.accentColor.length === 6
          ? normalizeHexColor(watchedColors.accentColor)
          : null,
    };
  }, [watchedColors]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="color-scheme-dialog"
    >
      <DialogTitle data-testid="color-scheme-dialog-title">
        Configure Color Scheme
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="color-scheme-form">
        <DialogContent>
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="color-scheme-error-alert"
            >
              {errorMessage || "Failed to save color scheme. Please try again."}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <ControlledColorField
              control={control}
              name="primaryColor"
              id="primary-color"
              label="Primary Color"
              errorMessage={errors.primaryColor?.message}
              helperText="Main brand color used for primary actions and elements"
            />

            <ControlledColorField
              control={control}
              name="secondaryColor"
              id="secondary-color"
              label="Secondary Color"
              clearable
              errorMessage={errors.secondaryColor?.message}
              helperText="Secondary brand color for accents and highlights"
            />

            <ControlledColorField
              control={control}
              name="accentColor"
              id="accent-color"
              label="Accent Color (Optional)"
              clearable
              errorMessage={errors.accentColor?.message}
              helperText="Optional accent color for special elements"
            />

            {/* Color Preview Section */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Color Preview
                  </Typography>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{ mt: 2 }}
                  >
                    {/* Primary Color Preview */}
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Primary Color
                      </Typography>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        sx={{
                          p: 2,
                          bgcolor: previewColors.primary,
                          borderRadius: 1,
                          color: "#FFFFFF",
                        }}
                        data-testid="primary-color-preview"
                      >
                        <Typography variant="body1" fontWeight={600}>
                          Sample Text on Primary
                        </Typography>
                        <Typography variant="body2" sx={{ ml: "auto" }}>
                          {previewColors.primary}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Secondary Color Preview */}
                    {previewColors.secondary && (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Secondary Color
                        </Typography>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          sx={{
                            p: 2,
                            bgcolor: previewColors.secondary,
                            borderRadius: 1,
                            color: "#FFFFFF",
                          }}
                          data-testid="secondary-color-preview"
                        >
                          <Typography variant="body1" fontWeight={600}>
                            Sample Text on Secondary
                          </Typography>
                          <Typography variant="body2" sx={{ ml: "auto" }}>
                            {previewColors.secondary}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Combined Preview */}
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Combined Preview
                      </Typography>
                      <Box
                        display="flex"
                        gap={1}
                        sx={{ mt: 1 }}
                        data-testid="combined-color-preview"
                      >
                        <Box
                          sx={{
                            flex: 1,
                            height: 80,
                            bgcolor: previewColors.primary,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#FFFFFF",
                            fontWeight: 600,
                          }}
                        >
                          Primary
                        </Box>
                        {previewColors.secondary && (
                          <Box
                            sx={{
                              flex: 1,
                              height: 80,
                              bgcolor: previewColors.secondary,
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#FFFFFF",
                              fontWeight: 600,
                            }}
                          >
                            Secondary
                          </Box>
                        )}
                        {previewColors.accent && (
                          <Box
                            sx={{
                              flex: 1,
                              height: 80,
                              bgcolor: previewColors.accent,
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#FFFFFF",
                              fontWeight: 600,
                            }}
                          >
                            Accent
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleResetToDefaults}
            startIcon={<RestartAlt />}
            disabled={isSubmitting}
            data-testid="reset-defaults-button"
          >
            Reset to Defaults
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            startIcon={<Cancel />}
            data-testid="color-scheme-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={<Save />}
            data-testid="color-scheme-save-button"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ColorSchemeDialog;
